import { type SchoolSignInDto, useSchoolDistrictGetSchoolSignIns } from "@crowdcoursing/api/openapi";
import { skipToken } from "@tanstack/react-query";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { DateTime } from "luxon";
import { useCallback, useMemo, useRef, useState } from "react";

import { type DateRangeValue } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { SchoolSignInsLineChartTemplate } from "~ui/templates/SchoolSignInsLineChartTemplate/SchoolSignInsLineChartTemplate";
import { SchoolSignInsTemplate } from "~ui/templates/SchoolSignInsTemplate/SchoolSignInsTemplate";

const tTitle = "School sign-ins";
const tSubtitle = "Daily sign-in counts for the selected range.";
const tDateRange = "Date range";
const tLoading = "Loading sign-ins…";
const tError = "Could not load sign-ins.";
const tYAxis = "Sign-ins";
const tXAxis = "Day";

function defaultWeekRange(): DateRangeValue {
  const now = DateTime.local();
  const weekStart = now.startOf("week").minus({ days: 7 });
  return {
    endDate: now,
    startDate: weekStart,
  };
}

/** Last calendar day shown on the chart and sent to the API — never after today. */
function effectiveChartRange(rangeStart: DateTime, rangeEnd: DateTime): { firstDay: DateTime; lastDay: DateTime } {
  const today = DateTime.local().startOf("day");
  const startDay = rangeStart.startOf("day");
  const endDay = rangeEnd.startOf("day");
  const lastDay = endDay <= today ? endDay : today;
  const firstDay = startDay <= lastDay ? startDay : lastDay;
  return { firstDay, lastDay };
}

function toPathDateTimes(range: DateRangeValue): { beginDate: string; endDate: string } | null {
  const { startDate, endDate } = range;
  if (!startDate || !endDate) {
    return null;
  }
  const { firstDay, lastDay } = effectiveChartRange(startDate, endDate);
  const now = DateTime.local();
  /** Last instant of the range, never after now (on “today”, use current time so the API window matches the chart). */
  const endInstant = lastDay.hasSame(now, "day") ? now : lastDay.endOf("day");
  return {
    beginDate: firstDay.startOf("day").toISO() ?? "",
    endDate: endInstant.toISO() ?? "",
  };
}

function aggregateSignInsByDay(
  signIns: SchoolSignInDto[],
  rangeStart: DateTime,
  rangeEnd: DateTime,
): { categories: string[]; counts: number[]; dayKeys: string[] } {
  const countsByDay = new Map<string, number>();
  for (const row of signIns) {
    if (!row.timestamp) {
      continue;
    }
    const day = DateTime.fromISO(row.timestamp).toFormat("yyyy-MM-dd");
    countsByDay.set(day, (countsByDay.get(day) ?? 0) + 1);
  }

  const { firstDay, lastDay } = effectiveChartRange(rangeStart, rangeEnd);

  const categories: string[] = [];
  const counts: number[] = [];
  const dayKeys: string[] = [];
  let d = firstDay;
  while (d <= lastDay) {
    const key = d.toFormat("yyyy-MM-dd");
    dayKeys.push(key);
    categories.push(d.toFormat("ccc, MMM d"));
    counts.push(countsByDay.get(key) ?? 0);
    d = d.plus({ days: 1 });
  }
  return { categories, counts, dayKeys };
}

export interface SchoolSignInsLineChartProps {
  schoolId: string;
}

export function SchoolSignInsLineChart({ schoolId }: SchoolSignInsLineChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [dateRange, setDateRange] = useState<DateRangeValue>(defaultWeekRange);
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  /** Calendar day (yyyy-MM-dd) for the slideout; set only when a chart point/day is clicked. */
  const [slideoutDayKey, setSlideoutDayKey] = useState<string | null>(null);

  const pathDates = useMemo(() => toPathDateTimes(dateRange), [dateRange]);

  const { data: signIns = [], status } = useSchoolDistrictGetSchoolSignIns(
    pathDates
      ? {
          pathParams: { bubbleId: schoolId },
          queryParams: {
            beginDate: pathDates.beginDate,
            endDate: pathDates.endDate,
          },
        }
      : skipToken,
  );

  const { categories, counts, dayKeys } = useMemo(() => {
    const start = dateRange.startDate;
    const end = dateRange.endDate;
    if (!start || !end) {
      return { categories: [], counts: [], dayKeys: [] as string[] };
    }
    return aggregateSignInsByDay(signIns, start, end);
  }, [dateRange.endDate, dateRange.startDate, signIns]);

  const slideoutSignIns = useMemo(() => {
    if (!slideoutDayKey) {
      return [];
    }
    return signIns.filter(
      (s) => s.timestamp && DateTime.fromISO(s.timestamp).toFormat("yyyy-MM-dd") === slideoutDayKey,
    );
  }, [signIns, slideoutDayKey]);

  /** Only a series point (a day on the line) opens the slideout — not empty plot area or axes. */
  const handlePointClick = useCallback(
    function (this: Highcharts.Point) {
      const key = dayKeys[this.index];
      if (key) {
        setSlideoutDayKey(key);
        setSlideoutOpen(true);
      }
    },
    [dayKeys],
  );

  const chartOptions = useMemo<Highcharts.Options>(
    () => ({
      accessibility: { enabled: false },
      chart: {
        height: 360,
        type: "line",
      },
      credits: { enabled: false },
      plotOptions: {
        line: {
          cursor: "pointer",
          lineWidth: 2,
          marker: { enabled: categories.length <= 31, radius: 4 },
          point: {
            events: {
              click: handlePointClick,
            },
          },
        },
      },
      series: [
        {
          data: counts,
          name: tYAxis,
          type: "line",
        },
      ],
      title: { text: undefined },
      tooltip: {
        shared: true,
        valueSuffix: " sign-ins",
      },
      xAxis: {
        categories,
        title: { text: tXAxis },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: { text: tYAxis },
      },
    }),
    [categories, counts, handlePointClick],
  );

  const chartStatus = status === "pending" ? "pending" : status === "error" ? "error" : "success";

  return (
    <SchoolSignInsLineChartTemplate
      chart={<HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />}
      chartStatus={chartStatus}
      dateRange={dateRange}
      dateRangeLabel={tDateRange}
      errorMessage={tError}
      loadingMessage={tLoading}
      slideout={
        <SchoolSignInsTemplate
          filterDayKey={slideoutDayKey}
          open={slideoutOpen}
          signIns={slideoutSignIns}
          onClose={() => {
            setSlideoutOpen(false);
            setSlideoutDayKey(null);
          }}
        />
      }
      subtitle={tSubtitle}
      title={tTitle}
      onDateRangeChange={setDateRange}
    />
  );
}
